import { useEffect, useRef } from "react";
import { RoomEvent, Track, RemoteParticipant, Participant, DataPacket_Kind, TranscriptionSegment, Room } from "livekit-client";
import { useRoomContext, useRemoteParticipants, useConnectionState } from "@livekit/components-react";
import type { TranscriptMessage, AgentMetrics, MetricsHistoryEntry, InterviewScores } from "./livekit-types";
import { isAgentParticipant } from "./livekit-types";

interface UseRoomEventHandlersProps {
    transcript: TranscriptMessage[];
    setTranscript: React.Dispatch<React.SetStateAction<TranscriptMessage[]>>;
    agentParticipant: RemoteParticipant | null;
    setAgentParticipant: React.Dispatch<React.SetStateAction<RemoteParticipant | null>>;
    receivedMessageIds: React.MutableRefObject<Set<string>>;
    setAgentStatus: React.Dispatch<React.SetStateAction<string>>;
    setAgentActivity: React.Dispatch<React.SetStateAction<string>>;
    setAgentMetrics: React.Dispatch<React.SetStateAction<AgentMetrics>>;
    setMetricsHistory: React.Dispatch<React.SetStateAction<MetricsHistoryEntry[]>>;
    onConnectionStateChange?: (connected: boolean) => void;
    contextSentRef?: React.MutableRefObject<boolean>;
    agentRequestedRef?: React.MutableRefObject<boolean>;
    onInterviewScores?: (scores: InterviewScores) => void;
}

/**
 * Custom hook that manages all LiveKit room event handlers:
 * - Disconnected / ParticipantConnected events
 * - TranscriptionReceived (primary transcription handler)
 * - DataReceived (metrics + activity via topic-based routing)
 * - Audio track subscription management
 * - Agent participant detection
 */
export function useRoomEventHandlers({
    transcript,
    setTranscript,
    agentParticipant,
    setAgentParticipant,
    receivedMessageIds,
    setAgentStatus,
    setAgentActivity,
    setAgentMetrics,
    setMetricsHistory,
    onConnectionStateChange,
    contextSentRef,
    agentRequestedRef,
    onInterviewScores,
}: UseRoomEventHandlersProps) {
    const room = useRoomContext();
    const connectionState = useConnectionState();
    const remoteParticipants = useRemoteParticipants();
    const isConnected = connectionState === "connected";

    // Track if TranscriptionReceived handler is registered to prevent duplicates
    const transcriptionHandlerRegisteredRef = useRef<boolean>(false);
    const transcriptionHandlerRef = useRef<((segments: TranscriptionSegment[], participant?: Participant) => void) | null>(null);
    const registeredRoomRef = useRef<Room | null>(null);

    // ─── Audio Track Subscription ───────────────────────────────────────────────

    useEffect(() => {
        if (!room || !isConnected) return;

        const logAudioTracks = () => {
            for (const participant of Array.from(room.remoteParticipants.values())) {
                if (!isAgentParticipant(participant)) continue;
                for (const publication of Array.from(participant.trackPublications.values())) {
                    if (publication.kind === "audio" || publication.source === Track.Source.Microphone) {
                        if (!publication.isSubscribed && publication.track) {
                            publication.setSubscribed(true);
                        }
                    }
                }
            }
        };

        const handleTrackPublished = (publication: any, participant: RemoteParticipant) => {
            if (publication.kind === "audio" || publication.source === Track.Source.Microphone) {
                const isAgent = isAgentParticipant(participant);

                // For agent audio tracks, force re-subscription to ensure fresh audio init
                if (isAgent) {
                    if (publication.isSubscribed) {
                        publication.setSubscribed(false);
                        setTimeout(() => {
                            publication.setSubscribed(true);
                        }, 50);
                    } else if (publication.track) {
                        publication.setSubscribed(true);
                    }
                } else {
                    if (!publication.isSubscribed && publication.track) {
                        publication.setSubscribed(true);
                    }
                }

                logAudioTracks();
            }
        };

        room.on(RoomEvent.TrackPublished, handleTrackPublished);
        room.on(RoomEvent.TrackSubscribed, (track: any, publication: any, participant: RemoteParticipant) => {
            // Auto-subscribe to audio tracks
        });

        // Periodic check (every 2 seconds) to ensure tracks stay subscribed
        const intervalId = setInterval(logAudioTracks, 2000);

        return () => {
            room.off(RoomEvent.TrackPublished, handleTrackPublished);
            clearInterval(intervalId);
        };
    }, [room, isConnected]);

    // ─── Agent Status Tracking ──────────────────────────────────────────────────

    useEffect(() => {
        if (isConnected && !agentParticipant) {
            setAgentStatus("waiting_for_agent");
        } else if (isConnected && agentParticipant && transcript.length > 0) {
            setAgentStatus("active");
        } else if (isConnected && agentParticipant) {
            setAgentStatus("agent_ready");
        }
    }, [isConnected, agentParticipant, transcript.length]);

    // ─── Room Events: Disconnect, Connect, Transcription, Data ──────────────────

    useEffect(() => {
        if (!room || !isConnected) return;

        const handleDisconnected = () => {
            setAgentParticipant(null);
            transcriptionHandlerRegisteredRef.current = false;
            transcriptionHandlerRef.current = null;
            registeredRoomRef.current = null;
            // Reset refs so context/history is resent and agent can be re-requested on reconnect
            if (contextSentRef) {
                contextSentRef.current = false;
            }
            if (agentRequestedRef) {
                agentRequestedRef.current = false;
            }
            setAgentStatus("disconnected");
            // Notify parent of disconnect
            if (onConnectionStateChange) {
                onConnectionStateChange(false);
            }
        };

        const handleParticipantConnected = (participant: Participant) => {
            const isAgent = isAgentParticipant(participant);

            if (isAgent) {
                setAgentParticipant(participant as RemoteParticipant);
                setAgentStatus("agent_joined");

                // Ensure audio tracks are subscribed when agent connects
                if (participant instanceof RemoteParticipant) {
                    Array.from(participant.trackPublications.values())
                        .filter((pub) => pub.kind === "audio" || pub.source === Track.Source.Microphone)
                        .forEach((publication) => {
                            if (!publication.isSubscribed && publication.track) {
                                publication.setSubscribed(true);
                            }
                        });
                }
            }
        };

        room.on(RoomEvent.Disconnected, handleDisconnected);
        room.on(RoomEvent.ParticipantConnected, handleParticipantConnected);

        // ─── Transcription Handler ──────────────────────────────────────────────

        const handleTranscriptionReceived = (segments: TranscriptionSegment[], participant?: Participant) => {
            for (const segment of segments) {
                if (!segment.text || !segment.text.trim()) continue;

                // Determine sender
                let sender: "user" | "agent" = "user";
                if (participant) {
                    sender = isAgentParticipant(participant) ? "agent" : "user";
                }

                const messageText = segment.text.trim();
                const segmentId = segment.id;
                const isFinal = segment.final;

                setTranscript((prev) => {
                    // Final segment with segment ID — replace any interim message
                    if (isFinal && segmentId) {
                        const existingIndex = prev.findIndex(
                            (msg) => msg.segmentId === segmentId && msg.sender === sender
                        );
                        const stableId = `msg-${sender}-${segmentId}`;

                        if (existingIndex !== -1) {
                            if (prev[existingIndex].text === messageText && prev[existingIndex].isFinal) {
                                return prev; // Skip duplicate
                            }
                            const updated = [...prev];
                            updated[existingIndex] = {
                                ...updated[existingIndex],
                                id: stableId,
                                text: messageText,
                                isFinal: true,
                                timestamp: updated[existingIndex].timestamp,
                            };
                            return updated;
                        }

                        // Check for duplicate text within recent messages
                        const now = Date.now();
                        const recentDuplicate = prev.find(
                            (msg) =>
                                msg.sender === sender &&
                                msg.text === messageText &&
                                msg.isFinal &&
                                now - msg.timestamp.getTime() < 3000
                        );
                        if (recentDuplicate) return prev;

                        return [
                            ...prev,
                            {
                                id: stableId,
                                text: messageText,
                                sender,
                                timestamp: new Date(),
                                segmentId,
                                isFinal: true,
                            },
                        ];
                    }

                    // Interim segment with segment ID
                    if (!isFinal && segmentId) {
                        const existingIndex = prev.findIndex(
                            (msg) => msg.segmentId === segmentId && msg.sender === sender
                        );

                        if (existingIndex !== -1 && prev[existingIndex].isFinal) {
                            return prev; // Already have final, ignore interim
                        }

                        const stableId = `msg-${sender}-${segmentId}`;
                        const interimMessage: TranscriptMessage = {
                            id: stableId,
                            text: messageText,
                            sender,
                            timestamp: new Date(),
                            segmentId,
                            isFinal: false,
                        };

                        if (existingIndex !== -1) {
                            const updated = [...prev];
                            updated[existingIndex] = interimMessage;
                            return updated;
                        }
                        return [...prev, interimMessage];
                    }

                    // Fallback: no segment ID — use deduplication
                    const now = Date.now();
                    const recentDuplicate = prev.find(
                        (msg) =>
                            msg.sender === sender &&
                            msg.text === messageText &&
                            msg.isFinal &&
                            now - msg.timestamp.getTime() < 2000
                    );
                    if (recentDuplicate) return prev;

                    const messageId = `${sender}:${messageText}`;
                    if (receivedMessageIds.current.has(messageId)) return prev;

                    receivedMessageIds.current.add(messageId);

                    // Bound the set to prevent unbounded memory growth
                    if (receivedMessageIds.current.size > 1000) {
                        const entries = Array.from(receivedMessageIds.current);
                        receivedMessageIds.current = new Set(entries.slice(-500));
                    }

                    const timestamp = Date.now();
                    const random = Math.random().toString(36).substring(2, 9);
                    return [
                        ...prev,
                        {
                            id: `msg-${sender}-${timestamp}-${random}`,
                            text: messageText,
                            sender,
                            timestamp: new Date(timestamp),
                            isFinal: true,
                        },
                    ];
                });
            }
        };

        // Only register once per room instance to prevent duplicates
        if (!transcriptionHandlerRegisteredRef.current || registeredRoomRef.current !== room) {
            if (registeredRoomRef.current && registeredRoomRef.current !== room && transcriptionHandlerRef.current) {
                try {
                    registeredRoomRef.current.off(RoomEvent.TranscriptionReceived, transcriptionHandlerRef.current);
                } catch (err) {
                    console.warn("Error removing old transcription handler:", err);
                }
            }

            room.on(RoomEvent.TranscriptionReceived, handleTranscriptionReceived);
            transcriptionHandlerRef.current = handleTranscriptionReceived;
            transcriptionHandlerRegisteredRef.current = true;
            registeredRoomRef.current = room;
        }

        // ─── Data Received Handler (Metrics + Activity via topic) ────────────────

        const handleDataReceived = (
            payload: Uint8Array,
            participant?: RemoteParticipant,
            kind?: DataPacket_Kind,
            topic?: string
        ) => {
            // Listen for agent metrics by topic (sent by agent.py with topic='agent_metrics')
            if (topic === "agent_metrics") {
                try {
                    const metricsData = JSON.parse(new TextDecoder().decode(payload));
                    setAgentMetrics(metricsData);

                    const now = Date.now();
                    setMetricsHistory((prev) => {
                        const newHistory = [
                            ...prev,
                            {
                                timestamp: now,
                                overall: metricsData.overallLatency,
                                llm: metricsData.llmLatency,
                                tts: metricsData.ttsLatency,
                            },
                        ];
                        // Keep only last 2 minutes of data
                        const twoMinutesAgo = now - 2 * 60 * 1000;
                        return newHistory.filter((m) => m.timestamp > twoMinutesAgo);
                    });
                } catch (err) {
                    console.error("[Metrics] Failed to parse metrics:", err);
                }
            }

            // Listen for agent activity messages by topic
            if (topic === "agent_activity") {
                try {
                    const activityData = JSON.parse(new TextDecoder().decode(payload));
                    if (activityData.message) {
                        setAgentActivity(activityData.message);
                    }
                } catch (err) {
                    console.error("[Activity] Failed to parse activity:", err);
                }
            }

            // Listen for interview scores from agent
            if (topic === "interview_scores") {
                try {
                    const scoresData: InterviewScores = JSON.parse(new TextDecoder().decode(payload));
                    console.log("📊 Interview scores received:", scoresData);
                    if (onInterviewScores) {
                        onInterviewScores(scoresData);
                    }
                } catch (err) {
                    console.error("[Scores] Failed to parse interview scores:", err);
                }
            }
        };

        room.on(RoomEvent.DataReceived, handleDataReceived);

        // ─── Find Agent Participant If Already Connected ────────────────────────

        const agentParticipants = remoteParticipants.filter((p) => isAgentParticipant(p));

        if (agentParticipants.length > 1) {
            console.warn(
                `⚠️ Multiple agent participants detected (${agentParticipants.length}).`,
                agentParticipants.map((p) => ({ identity: p.identity, name: p.name }))
            );
        }

        if (!agentParticipant) {
            const existingAgent = agentParticipants[0];
            if (existingAgent) {
                setAgentParticipant(existingAgent);
                Array.from(existingAgent.trackPublications.values())
                    .filter((pub) => pub.kind === "audio" || pub.source === Track.Source.Microphone)
                    .forEach((publication) => {
                        if (!publication.isSubscribed && publication.track) {
                            publication.setSubscribed(true);
                        }
                    });
            }
        }

        // ─── Cleanup ────────────────────────────────────────────────────────────

        return () => {
            room.off(RoomEvent.Disconnected, handleDisconnected);
            room.off(RoomEvent.ParticipantConnected, handleParticipantConnected);
            if (transcriptionHandlerRegisteredRef.current && transcriptionHandlerRef.current) {
                try {
                    room.off(RoomEvent.TranscriptionReceived, transcriptionHandlerRef.current);
                    transcriptionHandlerRegisteredRef.current = false;
                    transcriptionHandlerRef.current = null;
                } catch (err) {
                    console.warn("Error unregistering TranscriptionReceived handler:", err);
                }
            }
            room.off(RoomEvent.DataReceived, handleDataReceived);
        };
    }, [room, isConnected]); // Re-run when room connects/disconnects so handlers get registered

    return { room, isConnected, remoteParticipants };
}
